import * as THREE from "three";
import type { CookStatus } from "../game/food";

// Markers over the food on the heat, so you can tell from anywhere in the
// kitchen what needs you: a ring filling while it cooks, then FLIP, a check
// when it's done, and a warning when it's about to burn. Drawn on top of
// everything (seen through the hood and from the dish pit) and the same size
// on screen however far away you are.

const RING_STEPS = 16;
const SIZE = 128;

function draw(paint: (g: CanvasRenderingContext2D) => void): THREE.CanvasTexture {
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = SIZE;
  const g = canvas.getContext("2d")!;
  paint(g);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

function disc(g: CanvasRenderingContext2D, fill: string): void {
  g.beginPath();
  g.arc(SIZE / 2, SIZE / 2, SIZE / 2 - 6, 0, Math.PI * 2);
  g.fillStyle = fill;
  g.fill();
  g.lineWidth = 6;
  g.strokeStyle = "rgba(255,255,255,0.95)";
  g.stroke();
}

function word(g: CanvasRenderingContext2D, text: string, color: string, px: number): void {
  g.fillStyle = color;
  g.font = `900 ${px}px system-ui, sans-serif`;
  g.textAlign = "center";
  g.textBaseline = "middle";
  g.fillText(text, SIZE / 2, SIZE / 2 + 3);
}

function ring(fraction: number): THREE.CanvasTexture {
  return draw((g) => {
    const c = SIZE / 2;
    g.beginPath();
    g.arc(c, c, c - 14, 0, Math.PI * 2);
    g.fillStyle = "rgba(30,32,48,0.6)";
    g.fill();
    g.lineWidth = 14;
    g.strokeStyle = "rgba(255,255,255,0.3)";
    g.stroke();
    g.beginPath();
    g.arc(c, c, c - 14, -Math.PI / 2, -Math.PI / 2 + fraction * Math.PI * 2);
    g.strokeStyle = "#ffffff";
    g.lineCap = "round";
    g.stroke();
  });
}

let textures: { rings: THREE.CanvasTexture[]; status: Record<Exclude<CookStatus, "cooking">, THREE.CanvasTexture> } | null = null;

function shared() {
  textures ??= {
    rings: Array.from({ length: RING_STEPS + 1 }, (_, i) => ring(i / RING_STEPS)),
    status: {
      flip: draw((g) => {
        disc(g, "#f4b942");
        word(g, "FLIP", "#2b2d42", 40);
      }),
      done: draw((g) => {
        disc(g, "#18776c");
        g.beginPath();
        g.moveTo(36, 66);
        g.lineTo(56, 86);
        g.lineTo(94, 44);
        g.lineWidth = 14;
        g.lineCap = "round";
        g.lineJoin = "round";
        g.strokeStyle = "#ffffff";
        g.stroke();
      }),
      burning: draw((g) => {
        disc(g, "#d23a26");
        word(g, "!", "#ffffff", 80);
      }),
      burnt: draw((g) => {
        disc(g, "#2a1a14");
        word(g, "✕", "#ff8a7a", 64);
      }),
    },
  };
  return textures;
}

/** How big a marker is on screen: a fraction of the view's height. */
const SCREEN_SIZE = 0.07;

export class CookMarker {
  readonly sprite: THREE.Sprite;
  private readonly material: THREE.SpriteMaterial;

  constructor() {
    this.material = new THREE.SpriteMaterial({ map: shared().rings[0]!, depthTest: false, depthWrite: false, transparent: true, sizeAttenuation: false });
    this.sprite = new THREE.Sprite(this.material);
    this.sprite.renderOrder = 10;
    this.sprite.visible = false;
  }

  /** Show what the food under it needs (or hide it with null). Urgent ones pulse. */
  set(cook: { status: CookStatus; progress: number } | null, time: number): void {
    this.sprite.visible = !!cook;
    if (!cook) return;
    const t = shared();
    this.material.map = cook.status === "cooking" ? t.rings[Math.round(cook.progress * RING_STEPS)]! : t.status[cook.status];
    const urgent = cook.status === "flip" || cook.status === "burning";
    const pulse = urgent ? 1 + 0.18 * Math.max(0, Math.sin(time * 9)) : cook.status === "done" ? 1 + 0.06 * Math.sin(time * 5) : 0.8;
    this.sprite.scale.setScalar(SCREEN_SIZE * pulse);
  }
}
